import { number, object, string } from "yup";
import { OTHER_CONSTRUCTION_ITEM_ID } from "~/constants/DealConstructionConst";
import type { DealBilling, DealBillingContext } from "~/models/DealBilling";
import type {
	DealEstimation,
	DealEstimationContext
} from "~/models/DealEstimate";
import type { Deal } from "./Deal";
import type { DealConstructionItem } from "./DealConstructionItem";
import type { DealEstimationHeader } from "./DealEstimationHeader";
import type {
	DealEstimationHistory,
	DealEstimationHistoryContext,
	OperationType
} from "./DealEstimationHistory";
import { canEstimationAddStatus, isOwnStatus } from "./Status";

// このtypeのidは以下が入る
// DealEstimation → deal_estimations.id
// DealEstimationHistory → deal_estimation_histories.id
// DealBilling → deal_billings.id
export type DealConstructionBase = {
	companyAmount: number;
	companyChargePer: number;
	companyChargeWithPercentage: string;
	constructionItemId: number | null;
	customItemableId: number | null;
	enabled: boolean;
	id: number;
	note: string;
	otherName: string;
	ownerAmount: number;
	ownerChargePer: number;
	ownerChargeWithPercentage: string;
	point: string;
	renteeAmount: number;
	renteeChargePer: number;
	renteeChargeWithPercentage: string;
	seq: number;
	totalAmount: number;
	unit: number;
	unitAmount: number;
	unitName: string | null;
};

export type DealConstruction =
	| DealEstimation
	| DealEstimationHistory
	| DealBilling;

export type DealConstructionEdit = Pick<
	DealConstruction,
	| "otherName"
	| "point"
	| "unit"
	| "unitAmount"
	| "totalAmount"
	| "ownerChargePer"
	| "companyChargePer"
	| "renteeAmount"
	| "ownerAmount"
	| "companyAmount"
	| "note"
	| "enabled"
	| "constructionItemId"
	| "customItemableId"
> & {
	dealConstructionItem: DealConstructionItem | null;
	operationType: OperationType | null;
	renteeChargePer: number | "";
};

export type DealConstructionContextBase = {
	constructionTotalAmount: number;
	constructionTotalAmountWithTax: number;
	dealConstructions: DealConstruction[];
	totalAmount: number;
	totalAmountWithTax: number;
};
export type DealConstructionContext =
	| DealEstimationContext
	| DealBillingContext
	| DealEstimationHistoryContext;

export const isDealBillingContext = (
	context: DealConstructionContext
): context is DealBillingContext => "dealSettlements" in context;

export const checkConstructionAddDisabled = (
	constructionType: "estimation" | "billing",
	requestHeader: DealEstimationHeader | null,
	deal: Deal
): boolean => {
	// 非共有案件と請求情報は常に登録できる
	if (isOwnStatus(deal.statusId) || constructionType === "billing") {
		return false;
	}
	return requestHeader !== null || !canEstimationAddStatus(deal.statusId);
};

export const getConstructionItemName = (
	dealConstruction: DealConstruction
): string | null => {
	if (dealConstruction === null || dealConstruction.constructionItem === null) {
		return null;
	}
	return dealConstruction.constructionItem.id === OTHER_CONSTRUCTION_ITEM_ID
		? dealConstruction.otherName
		: dealConstruction.constructionItem.name;
};

export const dealConstructionContext = (
	ownManagement: boolean,
	requestSide: boolean,
	draftHistoryContext: DealEstimationHistoryContext,
	requestHistoryContext: DealEstimationHistoryContext | null,
	dealEstimationContext: DealEstimationContext
): DealConstructionContext => {
	// 申請前の見積りが存在する場合はそれを優先し、存在しない場合は確定済みの見積りを参照する
	const latestConstructionContext =
		draftHistoryContext.dealConstructions.length > 0
			? draftHistoryContext
			: dealEstimationContext;
	// 非共有案件
	if (ownManagement) return latestConstructionContext;
	// 見積り申請側
	if (requestSide) {
		return requestHistoryContext ?? latestConstructionContext;
	}
	// 通常案件で見積り申請側ではない
	return requestHistoryContext ?? dealEstimationContext;
};

export const validationSchemaConstruction = object().shape({
	companyChargePer: string()
		.required("入力してください")
		.test("min", "0以上で設定して下さい", function testValidation(): boolean {
			const { companyChargePer } = this.parent;
			return companyChargePer >= 0;
		}),
	dealConstructionItem: object()
		.nullable()
		.required("施工項目を設定してください"),
	ownerChargePer: string()
		.required("入力してください")
		.test("min", "0以上で設定して下さい", function testValidation(): boolean {
			const { ownerChargePer } = this.parent;
			return ownerChargePer >= 0;
		}),
	point: string().nullable(),
	renteeChargePer: string()
		.required("入力してください")
		.test("min", "0以上で設定して下さい", function testValidation(): boolean {
			const { renteeChargePer } = this.parent;
			return renteeChargePer >= 0;
		})
		.test(
			"hundredPer",
			"合計が100になるように入力してください",
			function testValidation(): boolean {
				const { ownerChargePer, renteeChargePer, companyChargePer } =
					this.parent;
				const sum =
					Number(ownerChargePer) +
					Number(renteeChargePer) +
					Number(companyChargePer);
				return sum === 100;
			}
		),
	unit: number()
		.min(1, "1以上で設定してください")
		.required("入力してください")
		.transform((value: string) => (Number.isNaN(value) ? Number("") : value)),
	unitAmount: number()
		.integer("整数で入力してください")
		.transform((value: string) => (Number.isNaN(value) ? null : value))
		.nullable()
		.required("入力してください")
});

export const buildDefaultConstruction = (
	operationType: "add" | "edit" | "delete"
): DealConstructionEdit => ({
	companyAmount: 0,
	companyChargePer: 0,
	constructionItemId: null,
	customItemableId: null,
	dealConstructionItem: null,
	enabled: true,
	note: "",
	operationType,
	otherName: "",
	ownerAmount: 0,
	ownerChargePer: 100,
	point: "",
	renteeAmount: 0,
	renteeChargePer: 0,
	totalAmount: 0,
	unit: 1,
	unitAmount: 0
});
